import styled, {css} from 'styled-components';

import {Label2, H3, H5, Body3} from '@bootcamp/web/src/components/Typography/next';

import {CheckmarkCircle} from '@styled-icons/fluentui-system-filled/CheckmarkCircle';
import {Dismiss} from '@styled-icons/fluentui-system-filled/Dismiss';
import {Wand} from '@styled-icons/fluentui-system-filled/Wand';
import {ArrowUturnLeft} from '@styled-icons/heroicons-solid/ArrowUturnLeft';
import {Copy} from '@styled-icons/fluentui-system-filled/Copy';

import datCalendar from '@bootcamp/shared/src/assets/images/ssg/placeholders/dat.png';
import nclexCalendar from '@bootcamp/shared/src/assets/images/ssg/placeholders/nclex.png';
import inbdeCalendar from '@bootcamp/shared/src/assets/images/ssg/placeholders/inbde.png';
import medSchoolCalendar from '@bootcamp/shared/src/assets/images/ssg/placeholders/med-school.png';
import mobileCalendar from '@bootcamp/shared/src/assets/images/ssg/placeholders/mobile.png';

export const Calendar = styled.div`
  display: grid;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  overflow: clip;

  ${({theme}) => theme.mediaQueries.tablet} {
    border-radius: 0px;
    border-bottom: 1px solid ${({theme}) => theme.colors.borderPalette.primary};
    padding-bottom: 8px;
    background: ${({theme}) => theme.colors.neutralsPalette.offWhite};
    gap: 2px;
  }
`;

export const CalendarWeek = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: white;
  border-top: ${({theme}) => `1px solid ${theme.colors.neutralsPalette.light}`};
  &:first-child {
    border-top: none;
  }
  ${({theme}) => theme.mediaQueries.tablet} {
    border-top: none;
    background: ${({theme}) => theme.colors.neutralsPalette.offWhite};
  }
`;

export const CalendarShadow = styled.div`
  box-shadow: ${({theme}) => theme.elevation.shadow.medium};
`;

export const CalendarContainer = styled.div`
  position: relative;
  border-radius: 8px;
  background: white;
  ${({theme}) => theme.mediaQueries.tablet} {
    overflow: hidden;
  }
`;

export const CalendarHeaderContainer = styled.div`
  background: ${({theme}) => theme.colors.neutralsPalette.offWhite};
  width: 100%;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  position: sticky;
  top: 0;
  z-index: 5;
`;

export const CalendarHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: ${({theme}) => theme.layouts.spacing.s} ${({theme}) => theme.layouts.spacing.m};
`;

export const CalendarHeaderButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({theme}) => theme.layouts.spacing.s};
`;
export const CalendarHeaderMonth = styled(H3)`
  margin: 0 auto;
`

export const CalenderHeaderDayRow = styled(CalendarWeek)`
  border-top: none;
  background: ${({theme}) => theme.colors.neutralsPalette.offWhite};
`;

export const CalendarHeaderDay = styled(Label2)`
  text-align: center;
  padding: 8px 0px;
  font-weight: 400;
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  ${({theme}) => theme.mediaQueries.tablet} {
    text-transform: capitalize;
    color: ${({theme}) => theme.colors.typographyPalette.tertiary};
    font-size: 12px;
    line-height: 12px;
    padding: 4px 0px;
  }
`;

export const MobileCalendarDay = styled(Label2)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 42px;
  height: 30px;
  margin: auto;
  position: relative;
  cursor: ${({theme, isPresent}) => !isPresent ? 'default' : 'pointer'};
  color: ${({theme, isPresent}) => !isPresent ? theme.colors.typographyPalette.tertiary : theme.colors.typographyPalette.primary};
  border-radius: 100px;

  ${({isSelected, theme}) => isSelected && css`
    background: ${theme.colors.brandPalette.royal.default};
    color: ${theme.colors.neutralsPalette.white};
  `}

  ${({multi, isSelected, theme}) => multi && css`
    background: ${isSelected ? '#EDEDF0' : '#EEF0FE'};
    text-decoration: ${isSelected ? 'line-through' : 'none'};
    color: ${isSelected ? '#85868E' : '#5127C4'};
    &:hover {
      text-decoration: line-through;
    }
  `}

  ${({isExamDay, theme}) => isExamDay && css`
    background: ${theme.colors.interfacePalette.red.light};
    color: ${theme.colors.interfacePalette.red.dark};
  `}

  ${({isDayOff, isPresent, isExamDay, theme, isSelected}) => isDayOff && isPresent && !isExamDay && css`
    background: ${isSelected ? theme.colors.brandPalette.royal.extraLight : theme.colors.borderPalette.primary};
    color: ${isSelected ? theme.colors.brandPalette.royal.dark : theme.colors.typographyPalette.secondary};
    text-decoration: line-through;
    border: ${isSelected ? '1px solid #5127C4' : 'none'};
  `}

  &:after {
    content: '';
    width: 4px;
    height: 4px;
    border-radius: 100px;
    background: ${({theme, isToday, isSelected}) => isToday && !isSelected ? theme.colors.brandPalette.royal.default : isToday ? theme.colors.neutralsPalette.white : 'transparent'};
    position: absolute;
    bottom: 3px;
  }
`;

const UpdateCompleteContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: ${({theme}) => theme.colors.brandPalette.royal.default};
  border-radius: 8px;
  box-shadow: ${({theme}) => theme.elevation.shadow.medium};
  gap: 12px;
  user-select: none;
  ${({themePalette, theme}) => themePalette == 'green' && css`
    background: ${theme.colors.interfacePalette.green.light};
    ${UpdateTitle} {
      color: ${theme.colors.interfacePalette.green.dark};
    }
    ${UpdateBody} {
      color: ${theme.colors.interfacePalette.green.dark};
    }
    ${CheckmarkCircleIcon} {
      color: ${theme.colors.interfacePalette.green.dark};
    }
  `}
`;

const CheckmarkCircleIcon = styled(CheckmarkCircle)`
  color: ${({theme}) => theme.colors.neutralsPalette.white};
  min-width: 16px;
  height: 16px;
`;
const WandIcon = styled(Wand)`
  color: ${({theme}) => theme.colors.neutralsPalette.white};
  min-width: 16px;
  width: 16px;
  height: 16px;
`;
const CopyIcon = styled(Copy)`
  color: ${({theme}) => theme.colors.neutralsPalette.white};
  min-width: 16px;
  width: 16px;
  height: 16px;
`;
const ArrowUturnLeftIcon = styled(ArrowUturnLeft)`
  color: ${({theme}) => theme.colors.neutralsPalette.white};
  min-width: 16px;
  width: 16px;
  height: 16px;
`;

const DismissIcon = styled(Dismiss)`
  color: ${({theme}) => theme.colors.neutralsPalette.white};
  background:rgba(255, 255, 255, 0.1);
  width: 32px;
  min-width: 32px;
  height: 32px;
  border-radius: 100px;
  cursor: pointer;
  padding: 8px;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const UpdateContent = styled.div`
  width: 100%;
`;

const UpdateTitle = styled(H5)`
  color: ${({theme}) => theme.colors.neutralsPalette.white};
`;
const UpdateBody = styled(Body3)`
  color: ${({theme}) => theme.colors.neutralsPalette.white};
  opacity: .7;
`;

export const UpdateComplete = ({closeToast, toastProps, title, body, iconName="checkmark", themePalette="royal", showDismiss=true}) => {
  const handleElementClick = e => {
    e.stopPropagation();
    e.preventDefault();
  };
  return (
    <UpdateCompleteContainer themePalette={themePalette} onClick={handleElementClick}>
      {iconName === "copy" ? <CopyIcon/> : iconName === "checkmark" ? <CheckmarkCircleIcon /> : iconName === "wand" ? <WandIcon /> : iconName === "reply" ? <ArrowUturnLeftIcon /> : null}
      <UpdateContent onClick={handleElementClick}>
        <UpdateTitle children={title} />
        <UpdateBody children={body} />
      </UpdateContent>
      {showDismiss && <DismissIcon onClick={closeToast} />}
    </UpdateCompleteContainer>
  );
};


export const placeholderMap = {
  oat: datCalendar,
  inbde: inbdeCalendar,
  nclex: nclexCalendar,
  'med-school': medSchoolCalendar,
  dat: datCalendar,
  mobile: mobileCalendar
}

export const testTitleMap = {
  oat: 'OAT',
  inbde: 'INBDE',
  nclex: 'NCLEX',
  'med-school': 'Step 1',
  dat: 'DAT',
}

export const studyScheduleLinkMap = {
  inbde: 'https://docs.google.com/document/d/1JL5kU_tUSoDMU0Km6umldqIZ1RUvpLEUhdtYMrDuhdw/edit?usp=sharing',
  dat: 'https://bootcamp.com/dat/aris-study-guide-for-dat-domination',
  'med-school': 'https://drive.google.com/file/d/1Bg4B2ya4gXmjlGw6AYXCd8A6Y2YAQW24/view?usp=share_link',
}
